import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ServiceTagsCard } from "@/components/routing/ServiceTagsCard";
import { MapsIntegrationCard } from "@/components/routing/MapsIntegrationCard";
import { TrackingPolicyCard } from "@/components/routing/TrackingPolicyCard";

export default function Settings() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("settings:manage");
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data || {})).catch(() => setS({})); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!s) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const field = (key, label, type = "input") => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {type === "textarea"
        ? <Textarea value={s[key] || ""} disabled={!canManage} onChange={(e) => setS({ ...s, [key]: e.target.value })} data-testid={`setting-${key}`} rows={3} />
        : <Input value={s[key] || ""} disabled={!canManage} onChange={(e) => setS({ ...s, [key]: e.target.value })} data-testid={`setting-${key}`} />}
    </div>
  );

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <PageHeader title="System Settings" subtitle="Organization profile shown across the public website and platform." />
      <div className="border border-border rounded-lg bg-card p-6 space-y-4">
        {field("org_name", "Organization Name")}
        {field("tagline", "Tagline")}
        {field("mission", "Mission Statement", "textarea")}
        <div className="grid sm:grid-cols-2 gap-4">
          {field("phone", "Phone")}
          {field("email", "Email")}
        </div>
        {field("address", "Address")}
        {canManage && (
          <div className="pt-2">
            <Button onClick={save} disabled={saving} data-testid="settings-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Settings"}
            </Button>
          </div>
        )}
      </div>
      {hasPerm("schedule:view") && <ServiceTagsCard />}
      <MapsIntegrationCard />
      <TrackingPolicyCard />
    </div>
  );
}
